import React, { useState } from 'react';
import { API_BASE } from '../config';
import { useTranslation } from '../translations';

function getCsrf() {
  const v = document.cookie.split(';').map(c => c.trim()).find(c => c.startsWith('csrf='));
  if (v) return decodeURIComponent(v.split('=').slice(1).join('='));
  return localStorage.getItem('csrfToken') || '';
}

function fetchWithCsrf(url, opts = {}) {
  const headers = { ...(opts.headers || {}) };
  const csrf = getCsrf();
  if (csrf) headers['X-CSRF-Token'] = csrf;
  return fetch(url, { ...opts, headers, credentials: 'include' });
}

export default function TwoFactorSetup({ onDone, onCancel, containerStyle }) {
  const { t } = useTranslation();
  const [step, setStep] = useState('start');     // 'start' | 'qr' | 'confirm' | 'backup'
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [manualKey, setManualKey] = useState('');
  const [totpCode, setTotpCode] = useState('');
  // Serwer wymaga aktualnego hasła przy włączaniu 2FA: samo ciasteczko sesji nie wystarcza.
  const [password, setPassword] = useState('');
  const [backupCodes, setBackupCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  const startSetup = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetchWithCsrf(`${API_BASE}/api/2fa/setup`, { method: 'POST' });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || t('twoFactorSetup.startError'));
        setLoading(false);
        return;
      }
      setQrDataUrl(data.qrDataUrl);
      setManualKey(data.manualEntryKey);
      setStep('qr');
    } catch {
      setError(t('twoFactorSetup.connectionError'));
    } finally {
      setLoading(false);
    }
  };

  const confirmSetup = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await fetchWithCsrf(`${API_BASE}/api/2fa/verify-setup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ totpCode: totpCode.replace(/\s/g, ''), password }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || t('twoFactorSetup.wrongCodeError'));
        setLoading(false);
        return;
      }
      setBackupCodes(data.backupCodes);
      setStep('backup');
    } catch {
      setError(t('twoFactorSetup.connectionError'));
    } finally {
      setLoading(false);
    }
  };

  const copyAll = () => {
    const text = backupCodes.join('\n');
    if (navigator.clipboard) {
      navigator.clipboard.writeText(text).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      });
    }
  };

  const base = containerStyle || {};

  if (step === 'start') return (
    <div style={base}>
      <p style={{ color: 'var(--text-muted)', marginBottom: 12 }}>
        {t('twoFactorSetup.intro')}
      </p>
      {error && <p style={{ color: 'var(--danger)', marginBottom: 8, fontSize: '0.875rem' }}>{error}</p>}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button onClick={startSetup} disabled={loading} style={btnPrimary}>
          {loading ? t('twoFactorSetup.loading') : t('twoFactorSetup.startButton')}
        </button>
        <button onClick={onCancel} style={btnSecondary}>{t('common.cancel')}</button>
      </div>
    </div>
  );

  if (step === 'qr') return (
    <div style={base}>
      <h4 style={{ margin: '0 0 8px', color: 'var(--text)' }}>{t('twoFactorSetup.step1Title')}</h4>
      <p style={{ color: 'var(--text-muted)', marginBottom: 12, fontSize: '0.875rem' }}>
        {t('twoFactorSetup.step1Intro')}
      </p>
      {qrDataUrl && (
        <img
          src={qrDataUrl}
          alt={t('twoFactorSetup.qrAlt')}
          style={{ width: 200, height: 200, display: 'block', marginBottom: 12, border: '1px solid var(--input-border)', borderRadius: 8 }}
        />
      )}
      <p style={{ color: 'var(--text-muted)', fontSize: '0.8rem', marginBottom: 4 }}>
        {t('twoFactorSetup.cantScan')}
      </p>
      <code style={{
        display: 'block', background: 'var(--panel-bg)', padding: '6px 10px',
        borderRadius: 6, fontSize: '0.875rem', wordBreak: 'break-all',
        color: 'var(--text)', marginBottom: 16, userSelect: 'all'
      }}>
        {manualKey}
      </code>
      <div style={{ display: 'flex', gap: 8 }}>
        <button onClick={() => { setTotpCode(''); setPassword(''); setStep('confirm'); }} style={btnPrimary}>
          {t('twoFactorSetup.next')}
        </button>
        <button onClick={onCancel} style={btnSecondary}>{t('common.cancel')}</button>
      </div>
    </div>
  );

  if (step === 'confirm') return (
    <div style={base}>
      <h4 style={{ margin: '0 0 8px', color: 'var(--text)' }}>{t('twoFactorSetup.step2Title')}</h4>
      <p style={{ color: 'var(--text-muted)', marginBottom: 12, fontSize: '0.875rem' }}>
        {t('twoFactorSetup.step2Intro')}
      </p>
      <form onSubmit={confirmSetup}>
        <label htmlFor="twofa-password" style={{ display: 'block', color: 'var(--text)', fontSize: '0.875rem', marginBottom: 4 }}>
          {t('twoFactorSetup.passwordLabel')}
        </label>
        <input
          id="twofa-password"
          data-cy="twofa-password"
          type="password"
          value={password}
          onChange={e => setPassword(e.target.value)}
          autoFocus
          autoComplete="current-password"
          style={{
            padding: '10px 12px', border: '1.5px solid var(--input-border)', borderRadius: 8,
            outline: 'none', display: 'block', marginBottom: 12, width: '100%', boxSizing: 'border-box',
            background: 'var(--input-bg)', color: 'var(--text)',
          }}
        />
        <input
          type="text"
          inputMode="numeric"
          pattern="[0-9 ]*"
          maxLength={7}
          placeholder="000 000"
          value={totpCode}
          onChange={e => setTotpCode(e.target.value.replace(/[^0-9 ]/g, ''))}
          autoComplete="one-time-code"
          style={{
            letterSpacing: '0.2em', textAlign: 'center', fontSize: '1.3rem',
            padding: '10px 16px', border: '1.5px solid var(--input-border)', borderRadius: 8,
            outline: 'none', display: 'block', marginBottom: 12, width: '100%', boxSizing: 'border-box',
            background: 'var(--input-bg)', color: 'var(--text)',
          }}
        />
        {error && <p style={{ color: 'var(--danger)', marginBottom: 8, fontSize: '0.875rem' }}>{error}</p>}
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            type="submit"
            disabled={loading || !password || totpCode.replace(/\s/g, '').length < 6}
            style={btnPrimary}
          >
            {loading ? t('twoFactorSetup.verifying') : t('twoFactorSetup.confirm')}
          </button>
          <button type="button" onClick={() => setStep('qr')} style={btnSecondary}>{t('twoFactorSetup.back')}</button>
        </div>
      </form>
    </div>
  );

  if (step === 'backup') return (
    <div style={base}>
      <h4 style={{ margin: '0 0 8px', color: 'var(--text)' }}>{t('twoFactorSetup.step3Title')}</h4>
      <div style={{
        background: 'var(--warning-bg)', border: '1px solid var(--warning-border)', borderRadius: 8,
        padding: '10px 14px', marginBottom: 12, fontSize: '0.875rem', color: 'var(--warning)'
      }}>
        <strong>{t('twoFactorSetup.importantLabel')}</strong> {t('twoFactorSetup.backupCodesWarning')}
      </div>
      <div style={{
        fontFamily: 'monospace', background: 'var(--panel-bg)', border: '1px solid var(--input-border)',
        borderRadius: 8, padding: '12px 16px', marginBottom: 12,
        display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 24px'
      }}>
        {backupCodes.map((c, i) => (
          <span key={i} style={{ color: 'var(--text)', fontSize: '0.9rem' }}>{c}</span>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button onClick={copyAll} style={btnSecondary}>
          {copied ? t('twoFactorSetup.copied') : t('twoFactorSetup.copyAll')}
        </button>
        <button onClick={onDone} style={btnPrimary}>
          {t('twoFactorSetup.done')}
        </button>
      </div>
    </div>
  );

  return null;
}

const btnPrimary = {
  background: 'var(--primary)',
  color: 'var(--primary-contrast)',
  border: 'none',
  borderRadius: 8,
  padding: '9px 18px',
  fontSize: '0.875rem',
  fontWeight: 500,
  cursor: 'pointer',
};

const btnSecondary = {
  background: 'transparent',
  color: 'var(--text-muted)',
  border: '1.5px solid var(--input-border)',
  borderRadius: 8,
  padding: '9px 18px',
  fontSize: '0.875rem',
  cursor: 'pointer',
};
